/**
 * Pure SLA helper functions shared by every officer workspace
 * (KYC, Underwriting, and any future workspace).
 *
 * No Angular DI — import and call directly.
 */

export type SlaClass = 'sla-red' | 'sla-orange' | 'sla-green';

/**
 * The two windows the colour ladder runs on, in minutes — mirrored from the backend's
 * `lendox.tasks.sla.{warning,critical}-window`.
 */
export interface SlaThresholdWindows {
  readonly warningWindowMinutes: number;
  readonly criticalWindowMinutes: number;
}

/**
 * The thresholds currently in force, or `null` before `SlaThresholdService` has loaded them.
 *
 * Module-level rather than injected because {@link deriveSlaClass} is a pure helper with no Angular
 * DI, called from six components across two workspaces. Making every one of them inject a service
 * to colour a row would be a much larger change for the same one-source guarantee — so the service
 * pushes once (`applySlaThresholds`) and every reader keeps calling the same function it always did.
 */
let activeThresholds: SlaThresholdWindows | null = null;

/**
 * Publishes the thresholds fetched from the backend. Called once per session by
 * `SlaThresholdService`; nothing else should call it outside a test.
 */
export function applySlaThresholds(thresholds: SlaThresholdWindows | null): void {
  activeThresholds = thresholds;
}

/** The thresholds in force, for the read-only Settings display. `null` until they have loaded. */
export function activeSlaThresholds(): SlaThresholdWindows | null {
  return activeThresholds;
}

/**
 * Derives a CSS class from an ISO-8601 SLA deadline string.
 *
 * Red    — less time remains than the CRITICAL window (a deadline already passed is always red).
 * Orange — less time remains than the WARNING window.
 * Green  — more time than that remains.
 *
 * Both windows come from backend configuration (`GET /api/platform/sla-thresholds`, EM-1558, epic
 * EM-1447 Story 15 — PRD-Underwriting §11.2), so retuning the ladder is a config change and a
 * restart rather than a front-end release. This is the ONE derivation; the KYC and Underwriting
 * queues share it and neither holds a copy.
 *
 * **Before the thresholds load there is deliberately no fallback ladder.** A compiled-in 2 h / 1 h
 * pair would be the second source Rule 17 forbids, and would keep colouring rows against numbers the
 * platform may no longer run. Until they arrive the helper reports only what is knowable without
 * configuration: a passed deadline is red, everything else is green.
 *
 * (Historical note for anyone reconciling this with the pre-Story-15 code: the ladder used to
 * escalate to red at BREACH and had no critical band at all, so PRD §6.14's "critical 1 h before the
 * deadline" described a ladder the platform did not run. It does now.)
 */
export function deriveSlaClass(slaDeadline: string): SlaClass {
  const remaining = new Date(slaDeadline).getTime() - Date.now();
  const thresholds = activeThresholds;
  if (!thresholds) return remaining < 0 ? 'sla-red' : 'sla-green';
  if (remaining < thresholds.criticalWindowMinutes * 60_000) return 'sla-red';
  if (remaining < thresholds.warningWindowMinutes * 60_000) return 'sla-orange';
  return 'sla-green';
}

/**
 * Formats an ISO-8601 SLA deadline string into a human-readable countdown
 * label (e.g. "3h 22m", "45m", "Expired").
 */
export function formatSlaLabel(slaDeadline: string): string {
  const remaining = new Date(slaDeadline).getTime() - Date.now();
  if (remaining <= 0) return 'Expired';
  const h = Math.floor(remaining / 3_600_000);
  const m = Math.floor((remaining % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * Comparator ordering queue rows by SLA urgency — MOST OVERDUE FIRST.
 *
 * Rows sort by ascending SLA deadline: a deadline further in the past (more
 * overdue) comes before a nearer one, which comes before an upcoming deadline,
 * so the most urgent dossiers surface at the top of the queue. Rows with no SLA
 * deadline sort last. `taskId` is the deterministic tiebreaker, so the order is
 * STABLE between loads (EM-907 — the backend returns rows in non-deterministic
 * Set-iteration order; this comparator makes the rendered order repeatable).
 */
export function compareBySlaUrgency(
  a: { slaDeadline: string | null; taskId: string },
  b: { slaDeadline: string | null; taskId: string },
): number {
  const at = slaSortKey(a.slaDeadline);
  const bt = slaSortKey(b.slaDeadline);
  if (at !== bt) return at - bt;
  return a.taskId.localeCompare(b.taskId);
}

/**
 * Maps an SLA deadline to its numeric sort key. A missing deadline — or an
 * unparseable date string that yields `NaN` — is treated as `+Infinity` so it
 * sorts LAST, keeping the comparator total and deterministic (EM-907).
 */
function slaSortKey(slaDeadline: string | null): number {
  if (!slaDeadline) return Number.POSITIVE_INFINITY;
  const t = new Date(slaDeadline).getTime();
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}
